import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as emailService from "../../../../services/emailService";
import { queryKeys } from "../../../../api/queryKeys";
import { toast } from "../../../../utils/notifications";
import type { ErrorLike } from "../../../../utils/errors";

/**
 * The "Check emails" button: asks the backend to check the mailbox, then
 * refreshes every tab and the counts - at once when the mail was processed,
 * after 10 s when it is still being processed in the background.
 */
export function useCheckEmails() {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastCheckedTime, setLastCheckedTime] = useState<string | null>(null);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all }); // Invalidate all tabs
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqCounts.all });
    setLastCheckedTime(new Date().toLocaleString());
  };

  const handleCheckEmails = async () => {
    if (isRefreshing) return;

    try {
      setIsRefreshing(true);
      const response = await emailService.checkEmails();

      if (response.processing) {
        toast({
          title: "Checking Emails",
          description: response.message || `Processing ${response.processing} new emails in background`,
          severity: "info"
        });

        // Refresh after a delay to allow for processing
        setTimeout(async () => {
          try {
            refreshAll();
          } catch (caught) {
            const refreshError = caught as ErrorLike;
            console.error('Error refreshing data:', refreshError);
            toast({
              title: "Refresh Failed",
              description: `Failed to refresh data after email processing: ${refreshError.message || "Network error or server timeout"}`,
              severity: "error"
            });
          }
        }, 10000); // Wait 10 seconds before refreshing
      } else if (response.processed && response.processed > 0) {
        refreshAll();
        toast({
          title: "Email Check Complete",
          description: response.message || `Processed ${response.processed} messages`,
          severity: "success"
        });
      } else {
        setLastCheckedTime(new Date().toLocaleString());
        toast({
          title: "Email Check Complete",
          description: response.message || "No new emails found",
          severity: "info"
        });
      }
    } catch (caught) {
      const error = caught as ErrorLike;
      console.error('Error checking emails:', error);

      const errorMessage = error.message || "Failed to check emails";
      const errorCode = error.status || error.code || '';

      toast({
        title: "Email Check Failed",
        description: `${errorMessage}${errorCode ? ` (Code: ${errorCode})` : ''}. Please check your network connection and try again.`,
        severity: "error"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return { isRefreshing, lastCheckedTime, handleCheckEmails };
}
